// Import a deck from Google Slides or Docs: OAuth (drive.file) -> Google Picker -> export to PDF.
// Pure Google glue; returns a PDF File for StudioCreate's onPickPdf. No pdf.ts / pipeline
// dependency; decoupled from Firebase Auth. Supports both Slides and Docs via the `kind` parameter.
// See docs/superpowers/specs/2026-07-01-google-slides-import-design.md.

export interface SlidesConfig {
  clientId: string;
  apiKey: string;
  appId: string;
}

/** Read + validate env config; null if any piece is missing (the UI hides the entry). */
export function slidesConfig(env: ImportMetaEnv = import.meta.env): SlidesConfig | null {
  const clientId = env.VITE_GOOGLE_OAUTH_CLIENT_ID;
  const apiKey = env.VITE_GOOGLE_API_KEY;
  const appId = env.VITE_GOOGLE_PROJECT_NUMBER;
  if (!clientId || !apiKey || !appId) return null;
  return { clientId, apiKey, appId };
}

export function slidesImportConfigured(env: ImportMetaEnv = import.meta.env): boolean {
  return slidesConfig(env) !== null;
}

const GIS_SRC = "https://accounts.google.com/gsi/client";
const GAPI_SRC = "https://apis.google.com/js/api.js";
const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";

/** Lazy-load an external script once; reject (and un-cache) if it fails to load. */
const scriptCache = new Map<string, Promise<void>>();
export function loadScript(src: string, doc: Document = document): Promise<void> {
  const cached = scriptCache.get(src);
  if (cached) return cached;
  const p = new Promise<void>((resolve, reject) => {
    const s = doc.createElement("script") as HTMLScriptElement;
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { scriptCache.delete(src); reject(new Error(`Failed to load ${src}`)); };
    doc.head.appendChild(s);
  });
  scriptCache.set(src, p);
  return p;
}

/** Request a Drive access token (drive.file) via GIS. Resolves null on cancel/denied.
 *  `loginHint` pins the consent popup to the account the user signed into the APP with —
 *  without it, GIS targets the browser's default Google session, which on multi-account
 *  machines is often the wrong account (observed in prod 2026-07-05). */
export function requestDriveToken(clientId: string, loginHint?: string): Promise<string | null> {
  return new Promise((resolve, reject) => {
    try {
      const client = google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: DRIVE_SCOPE,
        ...(loginHint ? { login_hint: loginHint } : {}),
        callback: (resp: { access_token?: string; error?: string }) => {
          resolve(resp.error || !resp.access_token ? null : resp.access_token);
        },
        error_callback: () => resolve(null), // popup closed / non-oauth error -> treat as cancel
      });
      client.requestAccessToken();
    } catch (e) {
      reject(e);
    }
  });
}

interface PickedFile { id: string; name: string; }

type DriveFileKind = "slides" | "docs" | "drive";

/** Show the Google Picker (incl. shared + Shared Drives). "drive" shows both
 *  Slides and Docs; "slides"/"docs" scope to one type. Null if dismissed. */
function showPicker(kind: DriveFileKind, token: string, apiKey: string, appId: string): Promise<PickedFile | null> {
  return new Promise((resolve) => {
    gapi.load("picker", () => {
      const viewIds =
        kind === "drive"
          ? [google.picker.ViewId.PRESENTATIONS, google.picker.ViewId.DOCUMENTS]
          : [kind === "docs" ? google.picker.ViewId.DOCUMENTS : google.picker.ViewId.PRESENTATIONS];
      const builder = new google.picker.PickerBuilder()
        .setAppId(appId)
        .setOAuthToken(token)
        .setDeveloperKey(apiKey)
        .setCallback((data: Record<string, unknown>) => {
          const action = data[google.picker.Response.ACTION];
          if (action === google.picker.Action.PICKED) {
            const doc = (data[google.picker.Response.DOCUMENTS] as Record<string, string>[])[0];
            resolve({ id: doc[google.picker.Document.ID], name: doc[google.picker.Document.NAME] });
          } else if (action === google.picker.Action.CANCEL) {
            resolve(null);
          }
        });
      for (const viewId of viewIds) {
        builder.addView(new google.picker.DocsView(viewId).setOwnedByMe(true));
        builder.addView(new google.picker.DocsView(viewId).setOwnedByMe(false).setEnableDrives(true));
      }
      builder.build().setVisible(true);
    });
  });
}

/** Export a Drive file to PDF. Prefers exportLinks (handles files > the ~10MB files.export cap).
 * @param kind "docs" for Google Docs, "slides" for Google Slides — affects filename fallback and error copy.
 */
export async function exportToPdf(
  fileId: string,
  name: string,
  token: string,
  fetchFn: typeof fetch = fetch,
  kind: DriveFileKind = "slides",
): Promise<File> {
  const id = encodeURIComponent(fileId);
  const metaRes = await fetchFn(
    `https://www.googleapis.com/drive/v3/files/${id}?fields=exportLinks&supportsAllDrives=true`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!metaRes.ok) throw new Error(`Drive metadata failed (${metaRes.status})`);
  const meta = (await metaRes.json()) as { exportLinks?: Record<string, string> };
  const url = meta.exportLinks?.["application/pdf"]
    ?? `https://www.googleapis.com/drive/v3/files/${id}/export?mimeType=application/pdf`;
  const pdfRes = await fetchFn(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!pdfRes.ok) {
    if (pdfRes.status === 403) {
      const noun = kind === "docs" ? "document" : kind === "drive" ? "file" : "deck";
      throw new Error(`This ${noun} can't be exported (too large, or download-restricted).`);
    }
    throw new Error(`${kind === "docs" ? "Docs" : kind === "drive" ? "Drive" : "Slides"} export failed (${pdfRes.status})`);
  }
  const blob = await pdfRes.blob();
  const base = name.replace(/\.[^.]+$/, "").trim() || (kind === "docs" ? "document" : kind === "drive" ? "file" : "deck");
  return new File([blob], `${base}.pdf`, { type: "application/pdf" });
}

/** Full flow: consent -> pick -> export. null = user cancel; throws on real failure.
 *  `onPicked` fires once the user has chosen a file — i.e. when the slow export +
 *  download begins — so callers can show progress for that wait (not for the
 *  interactive consent/Picker phase before it). */
async function importDriveFileAsPdf(
  kind: DriveFileKind,
  onPicked?: () => void,
  loginHint?: string,
): Promise<{ file: File; name: string } | null> {
  const cfg = slidesConfig();
  if (!cfg) throw new Error("Google Slides import is not configured.");
  await Promise.all([loadScript(GIS_SRC), loadScript(GAPI_SRC)]);
  const token = await requestDriveToken(cfg.clientId, loginHint);
  if (!token) return null;
  const picked = await showPicker(kind, token, cfg.apiKey, cfg.appId);
  if (!picked) return null;
  onPicked?.();
  const file = await exportToPdf(picked.id, picked.name, token, fetch, kind);
  return { file, name: picked.name };
}

export function importSlidesAsPdf(onPicked?: () => void, loginHint?: string): Promise<{ file: File; name: string } | null> {
  return importDriveFileAsPdf("slides", onPicked, loginHint);
}

export function importDocsAsPdf(onPicked?: () => void, loginHint?: string): Promise<{ file: File; name: string } | null> {
  return importDriveFileAsPdf("docs", onPicked, loginHint);
}

/** Combined Slides + Docs picker → PDF. Callers auto-detect deck vs doc from the
 *  exported PDF's page aspect (landscape → deck, portrait → doc). */
export function importDriveAsPdf(onPicked?: () => void, loginHint?: string): Promise<{ file: File; name: string } | null> {
  return importDriveFileAsPdf("drive", onPicked, loginHint);
}
